import * as Linking from "expo-linking";
import * as Sentry from "@sentry/react-native";
import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import {
  Animated,
  Easing,
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View
} from "react-native";
import { KeyboardAvoidingView } from "react-native-keyboard-controller";

import {
  deviceListLabel,
  spokenDeviceName,
  type DeviceRegistrationMetadata
} from "@recital-master/shared";

import {
  clearEmailForSignIn,
  readEmailForSignIn,
  storeEmailForSignIn
} from "@/auth/emailStorage";
import {
  sendEmailSignInLink,
  signInRateLimitCopy
} from "@/auth/emailLogin";
import {
  clearCurrentLoginRequestId,
  consumeLoginRequestInBackground,
  readCurrentLoginRequestId,
  subscribeToLoginRequestApproval
} from "@/auth/loginRequest";
import { useAuthState } from "@/auth/AuthProvider";
import { consumePostLoginReturn } from "@/auth/postLoginReturn";
import { CenteredCardBrand } from "@/components/CenteredCardBrand";
import { Button } from "@/design/Button";
import { Screen } from "@/design/primitives";
import { CheckIcon, MonitorIcon, PhoneIcon } from "@/design/icons";
import { Muted, Text } from "@/design/Text";
import { fonts, palette, radius, space } from "@/design/tokens";
import { useDemoMode } from "@/demo/DemoModeProvider";
import {
  approveLoginRequest,
  approveLoginRequestWithEmailLink,
  approveLoginRequestWithReviewCode,
  firebaseErrorCode,
  getLoginRequestPublic
} from "@/firebase/callables";
import { firebaseAuth } from "@/firebase/client";
import { useAppRouter } from "@/lib/appRouter";

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const DEMO_HOLD_MS = 2800;
const SUPPORT_EMAIL_LINK =
  "mailto:support@recitalmaster.com?subject=Recital%20Master%20support";
const useNativeAnimations = Platform.OS !== "web";
const APPROVAL_PREVIEW_DEVICE: DeviceRegistrationMetadata = {
  platform: "web",
  formFactor: "computer",
  operatingSystem: "macOS",
  modelCode: null,
  manufacturer: null,
  browserName: "Chrome"
};

function sanitizedAuthDiagnostic(error: unknown): { code: string; message: string } {
  const code = firebaseErrorCode(error) ?? "unknown";
  const rawMessage =
    typeof error === "object" && error != null && "message" in error
      ? String((error as Error).message)
      : "No error message";
  const message = rawMessage
    .replace(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g, "[redacted-email]")
    .replace(/([?&](?:oobCode|apiKey|token|key)=)[^&\s]+/gi, "$1[redacted]")
    .slice(0, 300);
  return { code, message };
}

function signInLinkErrorCopy(code: string): string {
  const normalized = code.replace(/^auth\//, "");
  if (
    normalized === "unavailable" ||
    normalized === "deadline-exceeded" ||
    normalized === "network-request-failed" ||
    normalized === "timeout"
  ) {
    return "We couldn’t connect to Recital Master. Check your connection and try again.";
  }
  if (normalized === "resource-exhausted" || normalized === "too-many-requests") {
    return "Too many sign-in attempts. Wait a few minutes and try again.";
  }
  return "We couldn’t send the link. Something went wrong on our end — try again in a moment.";
}

function continueParam(href: string, name: string): string | null {
  const url = new URL(href);
  const direct = url.searchParams.get(name);
  if (direct) return direct;
  const continueUrl = url.searchParams.get("continueUrl");
  if (!continueUrl) return null;
  try {
    return new URL(continueUrl).searchParams.get(name);
  } catch {
    return null;
  }
}

function actionCodeParam(href: string): string | null {
  return new URL(href).searchParams.get("oobCode");
}

export default function LoginScreen() {
  const loginPreview =
    __DEV__ &&
    Platform.OS === "web" &&
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("preview")
      : null;
  const approvalPreview =
    loginPreview === "approval";
  const sentPreview = loginPreview === "sent";
  /** Store-review fixed-code sign-in; unreachable without the real secrets. */
  const codePreview = loginPreview === "code";
  const [email, setEmail] = useState(
    sentPreview ? "you@example.com" : codePreview ? "appreview@example.com" : ""
  );
  const [status, setStatus] = useState<
    | "idle"
    | "sending"
    | "sent"
    | "code"
    | "code-submitting"
    | "confirming"
    | "finishing"
    | "finishing-slow"
    | "approve"
    | "approving"
    | "approved"
    | "expired"
    | "approval-error"
    | "error"
    | "link-failed"
  >(
    approvalPreview
      ? "approve"
      : sentPreview
        ? "sent"
        : codePreview
          ? "code"
          : "idle"
  );
  const [emailLinkHref, setEmailLinkHref] = useState<string | null>(
    approvalPreview ? "https://recitalmaster.com/login?oobCode=preview" : null
  );
  const [appRelayHref, setAppRelayHref] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [reviewCode, setReviewCode] = useState("");
  const [reviewCodeError, setReviewCodeError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(
    approvalPreview ? "preview" : null
  );
  const [linkRequestId, setLinkRequestId] = useState<string | null>(
    approvalPreview ? "preview" : null
  );
  const [didAuthenticateLink, setDidAuthenticateLink] = useState(false);
  const [enteringDemo, setEnteringDemo] = useState(false);
  const [approvalDevice, setApprovalDevice] = useState<DeviceRegistrationMetadata | null>(
    approvalPreview ? APPROVAL_PREVIEW_DEVICE : null
  );
  const [approvalLocationLabel, setApprovalLocationLabel] = useState<string | null>(
    approvalPreview ? "Winnipeg, MB" : null
  );
  const emailInputRef = useRef<TextInput>(null);
  const emailShake = useRef(new Animated.Value(0)).current;
  const handledNativeLinks = useRef(new Set<string>());
  const webDemoHoldTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { devMode } = useAuthState();
  const { enterDemo } = useDemoMode();
  const router = useAppRouter();

  async function proceedAfterLogin() {
    const returnPath = consumePostLoginReturn() ?? "/recitals";
    router.replace(returnPath as never);
  }

  async function finishSignIn(emailAddress: string, href: string) {
    if (!firebaseAuth || !emailAddress.trim()) return;
    const incomingRequestId = continueParam(href, "rid");
    setStatus(incomingRequestId ? "confirming" : "finishing");
    try {
      await signInWithEmailLink(firebaseAuth, emailAddress.trim().toLowerCase(), href);
      setDidAuthenticateLink(true);
      await clearEmailForSignIn();
      if (incomingRequestId) {
        const publicRequest = await getLoginRequestPublic(incomingRequestId);
        setApprovalDevice(publicRequest.device);
        setApprovalLocationLabel(publicRequest.locationLabel ?? null);
        const localRequestId = await readCurrentLoginRequestId();
        if (localRequestId === incomingRequestId) {
          await approveLoginRequest(incomingRequestId);
          consumeLoginRequestInBackground(incomingRequestId);
          await proceedAfterLogin();
          return;
        }
        setRequestId(incomingRequestId);
        setStatus("approve");
        return;
      }
      await proceedAfterLogin();
    } catch {
      if (incomingRequestId) {
        setStatus("expired");
        return;
      }
      // Sign-in links are single-use: retrying the same href can never
      // succeed, so drop it and fall back to sending a fresh link.
      setEmailLinkHref(null);
      setAppRelayHref(null);
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.history.replaceState(null, "", window.location.pathname);
      }
      setStatus("link-failed");
    }
  }

  async function prepareRemoteApproval(incomingRequestId: string) {
    setStatus("confirming");
    try {
      const publicRequest = await getLoginRequestPublic(incomingRequestId);
      if (publicRequest.status !== "pending") {
        setStatus("expired");
        return;
      }
      setApprovalDevice(publicRequest.device);
      setApprovalLocationLabel(publicRequest.locationLabel ?? null);
      setRequestId(incomingRequestId);
      setStatus("approve");
    } catch (error) {
      const code = firebaseErrorCode(error);
      setStatus(
        code === "deadline-exceeded" || code === "not-found" || code === "failed-precondition"
          ? "expired"
          : "approval-error"
      );
    }
  }

  useEffect(() => {
    if (Platform.OS !== "web" || !firebaseAuth || typeof window === "undefined") return;
    const href = window.location.href;
    if (!isSignInWithEmailLink(firebaseAuth, href)) return;
    setEmailLinkHref(href);
    const incomingRequestId = continueParam(href, "rid");
    setLinkRequestId(incomingRequestId);
    const appOriginated = continueParam(href, "from") === "app";
    if (appOriginated) setAppRelayHref(href);
    if (incomingRequestId && actionCodeParam(href)) {
      void prepareRemoteApproval(incomingRequestId);
      return;
    }
    readEmailForSignIn()
      .then((stored) => {
        if (stored) setEmail(stored);
        if (stored && (!appOriginated || incomingRequestId)) {
          return finishSignIn(stored, href);
        }
        return undefined;
      })
      .catch(() => {});
    // Only inspect the initial URL once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web" || !firebaseAuth || typeof window === "undefined") {
      return undefined;
    }
    if (isSignInWithEmailLink(firebaseAuth, window.location.href)) {
      return undefined;
    }

    let cancelled = false;
    Promise.all([
      readCurrentLoginRequestId(),
      readEmailForSignIn()
    ])
      .then(async ([storedRequestId, storedEmail]) => {
        if (!storedRequestId) return;
        const publicRequest = await getLoginRequestPublic(storedRequestId);
        if (cancelled) return;
        if (publicRequest.status === "consumed") {
          await clearCurrentLoginRequestId();
          return;
        }
        if (storedEmail) setEmail(storedEmail);
        setRequestId(storedRequestId);
        setStatus("sent");
      })
      .catch(async (error) => {
        const code = firebaseErrorCode(error);
        if (
          code === "deadline-exceeded" ||
          code === "not-found" ||
          code === "failed-precondition"
        ) {
          await clearCurrentLoginRequestId();
          return;
        }
        if (!cancelled) {
          setSendError(
            "We couldn’t restore your sign-in. Check your connection and reopen this page."
          );
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
    // Restore the one persisted handoff once when the browser reopens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !requestId ||
      (status !== "sent" && status !== "code" && status !== "code-submitting")
    ) {
      return undefined;
    }
    return subscribeToLoginRequestApproval(requestId, {
      onFinishing: () => setStatus("finishing"),
      onSlow: () => setStatus("finishing-slow"),
      onComplete: proceedAfterLogin,
      onError: () => {
        setSendError(
          "We couldn’t finish signing you in. Check your connection and try again."
        );
        setStatus("error");
      }
    });
    // The listener is intentionally replaced as the request state advances.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, status]);

  useEffect(() => {
    const auth = firebaseAuth;
    if (Platform.OS === "web" || !auth) return undefined;

    const receiveUrl = async (url: string | null) => {
      if (!url || handledNativeLinks.current.has(url)) return;
      let parsed: URL;
      try {
        parsed = new URL(url);
      } catch {
        return;
      }
      if (parsed.protocol !== "recitalmaster:" || parsed.hostname !== "auth") return;
      const href = parsed.searchParams.get("link");
      if (!href || !isSignInWithEmailLink(auth, href)) return;

      handledNativeLinks.current.add(url);
      setEmailLinkHref(href);
      setLinkRequestId(continueParam(href, "rid"));
      const stored = await readEmailForSignIn().catch(() => null);
      if (stored) {
        setEmail(stored);
        await finishSignIn(stored, href);
      } else {
        // Keep the existing form visible as the inline email fallback.
        setStatus("idle");
      }
    };

    void Linking.getInitialURL().then(receiveUrl);
    const subscription = Linking.addEventListener("url", ({ url }) => {
      void receiveUrl(url);
    });
    return () => subscription.remove();
    // Link intake is registered once for this route instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function shakeEmailInput() {
    emailShake.stopAnimation();
    emailShake.setValue(0);
    Animated.sequence(
      [6, -6, 5, -5, 3, -3, 0].map((toValue) =>
        Animated.timing(emailShake, {
          toValue,
          duration: 36,
          easing: Easing.linear,
          useNativeDriver: useNativeAnimations
        })
      )
    ).start();
  }

  function updateEmail(value: string) {
    setEmail(value);
    if (emailError) setEmailError(null);
  }

  async function submit() {
    const trimmedEmail = email.trim();
    const validationError = !trimmedEmail
      ? "Enter your email address."
      : !EMAIL_PATTERN.test(trimmedEmail)
        ? "That doesn't look like an email — check for typos."
        : null;
    if (validationError) {
      setEmailError(validationError);
      // Do not imperatively refocus here. On iOS 26 Simulator, asking an
      // already-active text field to become first responder again can block
      // UIKit in its pasteboard/keyboard XPC path and make the app appear
      // frozen. The inline alert and shake identify the field without
      // disturbing its existing focus state.
      shakeEmailInput();
      return;
    }
    if (emailLinkHref) {
      await storeEmailForSignIn(email);
      await finishSignIn(email, emailLinkHref);
      return;
    }
    setStatus("sending");
    setSendError(null);
    try {
      const response = await sendEmailSignInLink(email);
      if (response.status === "rate-limited") {
        setRequestId(null);
        setSendError(signInRateLimitCopy(response.retryAfterSeconds));
        setStatus("error");
        return;
      }
      setRequestId(response.requestId);
      setStatus(response.status === "code-required" ? "code" : "sent");
    } catch (error) {
      const diagnostic = sanitizedAuthDiagnostic(error);
      if (diagnostic.code === "not-found") {
        setStatus("idle");
        setEmailError("We can't find that email — use the one from your recital purchase.");
        shakeEmailInput();
        return;
      }
      console.error(
        `[auth] requestSignInLink failed code=${diagnostic.code} message=${diagnostic.message}`
      );
      Sentry.captureMessage("Email sign-in link request failed", {
        level: "error",
        tags: {
          authFlow: "requestSignInLink",
          firebaseCode: diagnostic.code,
          platform: Platform.OS
        },
        extra: { sanitizedMessage: diagnostic.message }
      });
      setSendError(signInLinkErrorCopy(diagnostic.code));
      setStatus("error");
    }
  }

  async function useDifferentEmail() {
    setRequestId(null);
    setEmail("");
    setEmailError(null);
    setReviewCode("");
    setReviewCodeError(null);
    setStatus("idle");
    await clearCurrentLoginRequestId();
    requestAnimationFrame(() => emailInputRef.current?.focus());
  }

  async function submitReviewCode() {
    if (!requestId || !reviewCode.trim()) {
      setReviewCodeError("Enter the sign-in code.");
      return;
    }
    setReviewCodeError(null);
    setStatus("code-submitting");
    try {
      await approveLoginRequestWithReviewCode(requestId, reviewCode.trim());
    } catch (error) {
      const code = firebaseErrorCode(error);
      setStatus("code");
      setReviewCodeError(
        code === "resource-exhausted"
          ? "Too many attempts. Wait a while and try again."
          : "That code is incorrect."
      );
    }
  }

  async function approve() {
    if (!requestId) return;
    setStatus("approving");
    if (approvalPreview) {
      setStatus("approved");
      return;
    }
    try {
      const code = emailLinkHref ? actionCodeParam(emailLinkHref) : null;
      if (Platform.OS === "web" && code) {
        await approveLoginRequestWithEmailLink(requestId, code);
      } else {
        await approveLoginRequest(requestId);
      }
      setStatus("approved");
    } catch (error) {
      const code = firebaseErrorCode(error);
      setStatus(
        code === "deadline-exceeded" || code === "not-found" || code === "failed-precondition"
          ? "expired"
          : "approval-error"
      );
    }
  }

  const remoteApprovalLink = emailLinkHref !== null && linkRequestId !== null;
  async function openDemo() {
    if (remoteApprovalLink || enteringDemo) return;
    setEnteringDemo(true);
    try {
      await enterDemo();
      router.replace("/recitals");
    } finally {
      setEnteringDemo(false);
    }
  }
  function cancelWebDemoHold() {
    if (webDemoHoldTimer.current) {
      clearTimeout(webDemoHoldTimer.current);
      webDemoHoldTimer.current = null;
    }
  }
  function beginWebDemoHold() {
    cancelWebDemoHold();
    webDemoHoldTimer.current = setTimeout(() => {
      webDemoHoldTimer.current = null;
      void openDemo();
    }, DEMO_HOLD_MS);
  }

  useEffect(
    () => () => {
      if (webDemoHoldTimer.current) clearTimeout(webDemoHoldTimer.current);
    },
    []
  );

  const approvalDeviceSubject = `Your ${spokenDeviceName(approvalDevice)}`;
  const approvalDeviceLabel = approvalDevice
    ? deviceListLabel(approvalDevice)
    : "Your other device";
  const browserLink = (
    <Pressable accessibilityRole="link" onPress={() => router.replace("/recitals")}>
      <Muted variant="small" style={styles.browserLink}>
        Or watch here in the browser
      </Muted>
    </Pressable>
  );
  const emailField = (
    <View style={styles.emailField}>
      <Animated.View style={{ transform: [{ translateX: emailShake }] }}>
        <TextInput
          ref={emailInputRef}
          accessibilityLabel="Email address"
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect={false}
          inputMode="email"
          spellCheck={false}
          textContentType="emailAddress"
          onBlur={() => setFocused(false)}
          onChangeText={updateEmail}
          onFocus={() => setFocused(true)}
          onSubmitEditing={submit}
          placeholder="you@example.com"
          placeholderTextColor={palette.faint}
          returnKeyType="send"
          style={[
            styles.input,
            focused && styles.inputFocused,
            emailError && styles.inputInvalid
          ]}
          value={email}
        />
      </Animated.View>
      {emailError ? (
        <Text
          accessibilityLiveRegion="polite"
          accessibilityRole="alert"
          color={palette.danger}
          variant="small"
        >
          {emailError}
        </Text>
      ) : null}
    </View>
  );

  return (
    <KeyboardAvoidingView
      behavior="padding"
      enabled={Platform.OS !== "web"}
      style={styles.keyboardAvoider}
    >
      <Screen keyboardShouldPersistTaps="handled">
        <View style={styles.wrap}>
          <View style={styles.card}>
          <CenteredCardBrand
            interactionProps={{
              delayLongPress: Platform.OS === "web" ? undefined : DEMO_HOLD_MS,
              disabled: remoteApprovalLink || enteringDemo,
              onLongPress: Platform.OS === "web" ? undefined : () => void openDemo(),
              onPointerCancel: Platform.OS === "web" ? cancelWebDemoHold : undefined,
              onPointerDown: Platform.OS === "web" ? beginWebDemoHold : undefined,
              onPointerLeave: Platform.OS === "web" ? cancelWebDemoHold : undefined,
              onPointerUp: Platform.OS === "web" ? cancelWebDemoHold : undefined
            }}
          />
          {!remoteApprovalLink ? (
            <>
              <Text variant="display" style={styles.headline}>
                {status === "finishing" || status === "finishing-slow"
                  ? "Signing you in."
                  : status === "sent"
                  ? "Check your email."
                  : status === "code" || status === "code-submitting"
                    ? "Enter your sign-in code."
                    : "Your recital is ready."}
              </Text>
            </>
          ) : null}
          {devMode ? (
            /* No Firebase config at all — the email form can't work, so it
               isn't shown. */
            <View style={styles.devNote}>
              <Muted variant="small" style={styles.devNoteText}>
                Design preview — Firebase isn’t configured in this environment.
              </Muted>
              <Button
                label="Continue to library"
                size="lg"
                onPress={() => router.replace("/recitals")}
              />
            </View>
          ) : (
            <>
              {remoteApprovalLink ? (
                <View style={styles.approvalBlock}>
                  <Text variant="display" style={styles.approvalTitle}>
                    {status === "approved"
                      ? `${approvalDeviceSubject} is ready.`
                      : status === "approval-error"
                        ? "We couldn't complete sign-in."
                      : status === "expired"
                        ? "This link has expired."
                        : status === "approve" || status === "approving"
                          ? "Approve this sign-in?"
                          : status === "idle"
                            ? "Confirm it's you."
                            : "Checking your request…"}
                  </Text>
                  <Muted style={styles.approvalCopy}>
                    {status === "approved"
                      ? "You can close this tab and return to Recital Master on that device to watch."
                      : status === "approval-error"
                        ? "Something went wrong on our side. Send a fresh link from your device and try once more."
                      : status === "expired"
                        ? "For your security, sign-in links work only once and expire quickly. Send a fresh one from the device you're signing in."
                        : status === "approve" || status === "approving"
                          ? "Your email link confirms it’s you. Only approve if you started this request."
                          : status === "idle"
                            ? "Enter the email address that received this link."
                            : "We're securely matching this link to the device that requested it."}
                  </Muted>
                  {status !== "idle" && status !== "expired" ? (
                    <View style={styles.devicePanel}>
                      <View style={[
                        styles.deviceIcon,
                        status === "approved" && styles.deviceIconApproved
                      ]}>
                        {status === "confirming" ? (
                          <ActivityIndicator size="small" color={palette.textSoft} />
                        ) : status === "approved" ? (
                          <CheckIcon size={21} color={palette.inkOnAccent} />
                        ) : approvalDevice?.platform === "web" ? (
                          <MonitorIcon size={18} color={palette.textSoft} />
                        ) : (
                          <PhoneIcon size={18} color={palette.textSoft} />
                        )}
                      </View>
                      <View style={styles.deviceCopy}>
                        <Text variant="bodyStrong">
                          {status === "confirming"
                            ? "Finding your device…"
                            : approvalDeviceLabel}
                        </Text>
                        {approvalLocationLabel ? (
                          <Muted variant="small">{approvalLocationLabel}</Muted>
                        ) : null}
                      </View>
                    </View>
                  ) : null}
                  {status === "idle" ? (
                    <>
                      {emailField}
                      <Button label="Continue" size="lg" onPress={submit} />
                    </>
                  ) : null}
                  {status === "approve" || status === "approving" ? (
                    <Button
                      label="Approve sign-in"
                      size="lg"
                      onPress={approve}
                      loading={status === "approving"}
                      style={styles.approvalButton}
                    />
                  ) : null}
                  {didAuthenticateLink && (status === "approved" || status === "expired")
                    ? browserLink
                    : null}
                </View>
              ) : (
              <>
              <Muted style={styles.copy}>
                {status === "finishing-slow"
                  ? "This is taking longer than usual, but we’re still connected. Keep this page open."
                  : status === "finishing"
                    ? "Your email is confirmed. We’re opening your recital library."
                  : status === "sent"
                  ? `We emailed a sign-in link to ${email.trim()}.`
                  : status === "code" || status === "code-submitting"
                    ? "Use the code provided for your account."
                    : appRelayHref
                      ? "Open this sign-in link in the Recital Master app, or continue here in your browser."
                      : "Enter the email you used for recital access and we’ll send you a sign-in link. No password to remember."}
              </Muted>
              {appRelayHref ? (
                <Button
                  label="Open the Recital Master app"
                  size="lg"
                  onPress={() =>
                    Linking.openURL(
                      `recitalmaster://auth?link=${encodeURIComponent(appRelayHref)}`
                    )
                  }
                />
              ) : null}
              {appRelayHref ? (
                <Muted variant="small" style={styles.hint}>
                  Or continue in the browser
                </Muted>
              ) : null}
              {status === "finishing" || status === "finishing-slow" ? (
                <View
                  accessibilityLabel="Signing in"
                  accessibilityLiveRegion="polite"
                  style={styles.waitingRow}
                >
                  <ActivityIndicator
                    size={Platform.OS === "web" ? 16 : "small"}
                    color={palette.textSoft}
                  />
                  <Muted variant="small">
                    {status === "finishing-slow"
                      ? "Still signing you in…"
                      : "Finishing sign-in…"}
                  </Muted>
                </View>
              ) : status !== "sent" && status !== "code" && status !== "code-submitting" ? (
              <>
              {emailField}
              <Button
                label={
                  emailLinkHref
                    ? appRelayHref
                      ? "Continue in browser"
                      : "Finish sign-in"
                    : "Continue"
                }
                size="lg"
                variant={appRelayHref ? "secondary" : undefined}
                onPress={submit}
                loading={status === "sending"}
              />
              </>
              ) : status === "sent" ? (
                <View style={styles.waitingBlock}>
                  <View style={styles.waitingRow}>
                    <ActivityIndicator
                      size={Platform.OS === "web" ? 16 : "small"}
                      color={palette.textSoft}
                    />
                    <Muted variant="small">Waiting for approval…</Muted>
                  </View>
                  <Pressable accessibilityRole="link" onPress={useDifferentEmail}>
                    <Text variant="small" color={palette.textSoft} style={styles.hint}>
                      Use a different email
                    </Text>
                  </Pressable>
                </View>
              ) : (
                <View style={styles.waitingBlock}>
                  <View style={styles.emailField}>
                    <TextInput
                      accessibilityLabel="Sign-in code"
                      autoCapitalize="none"
                      autoCorrect={false}
                      onChangeText={(value) => {
                        setReviewCode(value);
                        if (reviewCodeError) setReviewCodeError(null);
                      }}
                      onSubmitEditing={() => void submitReviewCode()}
                      placeholder="Sign-in code"
                      placeholderTextColor={palette.faint}
                      returnKeyType="done"
                      // Deliberately not masked. The only person who ever types
                      // here is a store reviewer copying from submission notes;
                      // masking buys no security against any real threat and
                      // hides the typo that would otherwise read to them as a
                      // failed login.
                      style={[styles.input, reviewCodeError && styles.inputInvalid]}
                      value={reviewCode}
                    />
                    {reviewCodeError ? (
                      <Text
                        accessibilityLiveRegion="polite"
                        accessibilityRole="alert"
                        color={palette.danger}
                        variant="small"
                      >
                        {reviewCodeError}
                      </Text>
                    ) : null}
                    <Button
                      label="Sign in"
                      size="lg"
                      loading={status === "code-submitting"}
                      onPress={submitReviewCode}
                    />
                  </View>
                  <Pressable accessibilityRole="link" onPress={useDifferentEmail}>
                    <Text variant="small" color={palette.textSoft} style={styles.hint}>
                      Use a different email
                    </Text>
                  </Pressable>
                </View>
              )}
              </>
              )}
              {status === "error" ? (
                <View style={styles.errorBlock}>
                  <Text variant="small" color={palette.danger} style={styles.hint}>
                    {sendError ??
                      "We couldn’t send the link. Something went wrong on our end — try again in a moment."}
                  </Text>
                </View>
              ) : null}
              {status === "link-failed" ? (
                <Text variant="small" color={palette.danger} style={styles.hint}>
                  That sign-in link didn’t work — it may be expired or already
                  used. Check your email address and we’ll send a fresh one.
                </Text>
              ) : null}
            </>
          )}
          </View>
          <View style={styles.supportRow}>
            <Muted variant="small">Need help? Email</Muted>
            <Pressable
              accessibilityHint="Opens your email app"
              accessibilityRole="link"
              hitSlop={space.sm}
              onPress={() => void Linking.openURL(SUPPORT_EMAIL_LINK)}
              style={styles.supportLink}
            >
              <Text
                variant="small"
                color={palette.muted}
                style={styles.supportLinkText}
              >
                support@recitalmaster.com
              </Text>
            </Pressable>
          </View>
        </View>
      </Screen>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  keyboardAvoider: {
    flex: 1
  },
  wrap: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    minHeight: 560,
    paddingVertical: space.xxxl
  },
  card: {
    backgroundColor: palette.surface,
    borderColor: palette.hairline,
    borderRadius: radius.xl,
    borderWidth: 1,
    gap: space.lg,
    maxWidth: 460,
    padding: space.xxl,
    width: "100%"
  },
  supportRow: {
    alignItems: "baseline",
    flexDirection: "row",
    gap: space.xs,
    marginTop: space.lg
  },
  supportLink: {
    flexShrink: 0
  },
  supportLinkText: {
    textDecorationLine: "underline"
  },
  headline: {
    textAlign: "center"
  },
  copy: {
    textAlign: "center"
  },
  input: {
    backgroundColor: palette.surfaceSunken,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    color: palette.text,
    fontFamily: fonts.body,
    fontSize: 16,
    paddingHorizontal: space.lg,
    paddingVertical: 14,
    ...Platform.select({ web: { outlineStyle: "none" }, default: {} })
  } as never,
  inputFocused: {
    backgroundColor: palette.surface,
    borderColor: "rgba(143, 168, 250, 0.55)",
    ...Platform.select({
      web: { boxShadow: "0 0 0 3px rgba(143, 168, 250, 0.15)" },
      default: {}
    })
  } as never,
  inputInvalid: {
    borderColor: palette.danger
  },
  emailField: {
    gap: space.sm
  },
  hint: {
    textAlign: "center"
  },
  errorBlock: {
    alignItems: "center",
    gap: space.xs
  },
  browserLink: {
    textAlign: "center",
    textDecorationLine: "underline"
  },
  devNote: {
    borderTopColor: palette.hairline,
    borderTopWidth: 1,
    gap: space.md,
    marginTop: space.sm,
    paddingTop: space.lg
  },
  devNoteText: {
    textAlign: "center"
  },
  approvalBlock: {
    alignItems: "stretch"
  },
  approvalTitle: {
    textAlign: "center"
  },
  approvalCopy: {
    alignSelf: "center",
    marginTop: space.md,
    maxWidth: 390,
    textAlign: "center"
  },
  devicePanel: {
    alignItems: "center",
    backgroundColor: palette.coolWashSubtle,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    flexDirection: "row",
    gap: space.md,
    marginTop: space.xl,
    padding: space.md
  },
  deviceIcon: {
    alignItems: "center",
    backgroundColor: palette.coolWashStrong,
    borderColor: palette.hairlineStrong,
    borderRadius: radius.md,
    borderWidth: 1,
    height: 36,
    justifyContent: "center",
    width: 36
  },
  deviceIconApproved: {
    backgroundColor: palette.cyan,
    borderColor: "transparent"
  },
  deviceCopy: {
    flex: 1,
    gap: 2
  },
  approvalButton: {
    marginTop: space.xl
  },
  waitingBlock: { gap: space.lg },
  waitingRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm,
    justifyContent: "center"
  }
});
